/**
 * Client-side image compression using the Canvas API.
 *
 * Scales the image down so its longest side fits within `maxDimension` pixels
 * (preserving aspect ratio) and re-encodes it as JPEG at the given quality.
 *
 * @param {File} file - The image File to compress
 * @param {Object} [options]
 * @param {number} [options.maxDimension=1920] - Max width/height in pixels
 * @param {number} [options.quality=0.8] - JPEG quality (0-1)
 * @returns {Promise<File>} Compressed File (or the original if compression fails / isn't needed)
 */
export const compressImage = (file, { maxDimension = 1920, quality = 0.8 } = {}) => {
  return new Promise((resolve) => {
    // Only compress raster image types the canvas can handle
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/i)) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const { width, height } = img;

      // Skip if already within the max dimension
      if (width <= maxDimension && height <= maxDimension) {
        resolve(file);
        return;
      }

      // Calculate scaled dimensions preserving aspect ratio
      let newWidth, newHeight;
      if (width > height) {
        newWidth = maxDimension;
        newHeight = Math.round((height / width) * maxDimension);
      } else {
        newHeight = maxDimension;
        newWidth = Math.round((width / height) * maxDimension);
      }

      // Draw to an offscreen canvas
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            // toBlob failed — fall back to original
            resolve(file);
            return;
          }

          // Build a new File that preserves the original's custom metadata
          const compressed = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: file.lastModified,
          });

          // Copy any custom metadata properties attached to the original File
          if (file.originalName) compressed.originalName = file.originalName;
          if (file.sectionIndex !== undefined) compressed.sectionIndex = file.sectionIndex;
          if (file.areaIndex !== undefined) compressed.areaIndex = file.areaIndex;
          if (file.uploadTimestamp) compressed.uploadTimestamp = file.uploadTimestamp;

          resolve(compressed);
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Corrupt or unsupported image — return the original file
      resolve(file);
    };

    img.src = url;
  });
};
